import React from "react";
import { t } from "@lingui/core/macro";
import HebcalService from "../../../services/hebcal";

/**
 * Socle commun des horaires halakhiques (zmanim) du thème 2026.
 *
 * Repris de `themes/Travel/JewishZmanim` (mêmes zmanim, même mapping FR), mais
 * SANS l'appel réseau à timeapi.io qu'utilisait Travel : `HebcalService` expose
 * déjà `getTimezone()` (tz-lookup, hors ligne, mémoïsé). Un fetch externe sans
 * timeout ni cache pouvait échouer silencieusement et retomber sur le fuseau du
 * navigateur — décalage horaire pour tout visiteur hors du fuseau de l'org.
 *
 * Tout est calculé CÔTÉ FRONT à partir de `org.lat/long` + `org.zmanimShitat`.
 * Aucune dépendance API.
 */

/** Noms FR des zmanim hébreux renvoyés par HebcalService. */
export function getZmanimLabels() {
    return {
        "עלות השחר 120 דקות": t`Aube (120 min)`,
        "עלות השחר 72 דקות":  t`Aube (72 min)`,
        "עלות השחר":          t`Aube`,
        "תפילין ושמע":        t`Tefillin & Chéma`,
        "נץ החמה":            t`Lever du soleil`,
        "סוף זמן ק״ש":        t`Heure (limite) du Kriat Chéma (Admour Hazaken)`,
        "סוף זמן ק״ש מ״א":    t`Heure (limite) du Kriat Chéma (Maguen Avraham)`,
        "סוף זמן ק״ש גר״א":   t`Heure (limite) du Kriat Chéma (Admour Hazaken)`,
        "סוף זמן תפילה":       t`Heure (limite) de la prière (Amida) (Gra)`,
        "סוף זמן תפילה מ״א":   t`Heure (limite) de la prière (Maguen Avraham)`,
        "סוף זמן תפילה גר״א":  t`Heure (limite) de la prière (Amida) (Gra)`,
        "חצות היום":           t`Hatsot (milieu de la journée)`,
        "מנחה גדולה":          t`Min'ha Gdola`,
        "מנחה קטנה":          t`Min'ha Ketana`,
        "פלג המנחה":           t`Plag haMinha`,
        "שקיעת החמה":          t`Coucher du soleil`,
        "צאת הכוכבים":         t`Nuit (sortie des étoiles)`,
        "צאת דאורייתא":        t`Nuit - Tsèt deoraïta`,
        "צאת הכוכבים ר'ת":     t`Nuit - Rabbenou Tam`,
        "חצות לילה":           t`Minuit halachique`,
    };
}

/** HH:MM dans le fuseau de l'org (`tz` = nom IANA). */
export function fmtTime(date, locale, tz) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "—";
    try {
        return new Intl.DateTimeFormat(locale || "fr", {
            hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz || undefined,
        }).format(date);
    } catch (e) {
        return "—";
    }
}

/** Date grégorienne longue ("lundi 21/07/2026") dans le fuseau de l'org. */
export function fmtGreg(date, locale, tz) {
    if (!date || isNaN(date.getTime())) return "";
    try {
        return new Intl.DateTimeFormat(locale || "fr", {
            weekday: "long", day: "2-digit", month: "2-digit", year: "numeric",
            timeZone: tz || undefined,
        }).format(date);
    } catch (e) {
        return "";
    }
}

/** Date courte ("21/07/2026") dans le fuseau de l'org. */
export function fmtShortDate(date, locale, tz) {
    if (!date || isNaN(date.getTime())) return "—";
    try {
        return new Intl.DateTimeFormat(locale || "fr", {
            day: "2-digit", month: "2-digit", year: "numeric", timeZone: tz || undefined,
        }).format(date);
    } catch (e) {
        return "—";
    }
}

/**
 * Hook : calcule les zmanim d'une date donnée pour la position de l'org.
 *
 * Le calcul tourne CLIENT-SIDE uniquement (`mounted`) : il dépend de l'heure
 * courante (marquage du prochain zman) et produirait sinon un mismatch
 * d'hydratation entre serveur et client — même précaution que DailyOffices.
 *
 * Renvoie `null` si la position de l'org est inconnue (aucun calcul possible).
 */
export function useZmanim(org, date) {
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => { setMounted(true); }, []);

    return React.useMemo(() => {
        if (!mounted) return null;
        if (!org || org.lat == null || org.long == null) return null;
        try {
            const hebcal = new HebcalService(org, new Date(), org.lat, org.long);
            hebcal.generateHebCal();

            const ref = new Date(date);
            const safe = (fn) => { try { return fn(); } catch (e) { return null; } };
            const period = safe(() => hebcal.getNextRestPeriod(new Date(ref)));

            return {
                zmanim: safe(() => hebcal.getZmanimDay(new Date(ref))) || [],
                hdate: safe(() => hebcal.hdate(new Date(ref))),
                period,
                entree: period?.start || null,
                sortie: period?.end || null,
                parasha: period?.occasion.parasha?.latin || null,
                tz: safe(() => hebcal.getTimezone()),
            };
        } catch (e) {
            return null;
        }
    }, [mounted, org?.lat, org?.long, org?.israel, org?.zmanimShitat, org?.chabadCandleLightingMinutes, date]);
}
